import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { Button } from './ui/Button';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
    }

    public handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.reload();
    };

    public handleGoHome = () => {
        const hash = window.location.hash;
        if (hash.includes('/aurora')) {
            window.location.hash = '#/aurora/dashboard';
        } else if (hash.includes('/psych')) {
            window.location.hash = '#/psych/dashboard';
        } else if (hash.includes('/patient')) {
            window.location.hash = '#/patient/dashboard';
        } else {
            window.location.hash = '#/login';
        }
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-50 dark:bg-darkBg text-center animate-fade-in">
                    <div className="bg-white dark:bg-darkSurface p-8 rounded-2xl shadow-xl max-w-md w-full border border-slate-100 dark:border-darkBorder">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                            <AlertTriangle size={32} />
                        </div>

                        <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
                            Ops! Algo deu errado.
                        </h1>

                        <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm leading-relaxed">
                            Desculpe, encontramos um erro inesperado. Tente recarregar a página ou voltar para o início.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <div className="bg-slate-100 dark:bg-slate-900 p-3 rounded-lg text-left mb-6 overflow-auto max-h-32 text-xs text-red-500 font-mono border border-slate-200 dark:border-slate-800">
                                {this.state.error.toString()}
                            </div>
                        )}

                        <div className="flex flex-col gap-3">
                            <Button
                                onClick={this.handleReset}
                                variant="primary"
                                className="w-full justify-center gap-2"
                            >
                                <RefreshCcw size={16} /> Tentar Novamente
                            </Button>

                            <Button
                                onClick={this.handleGoHome}
                                variant="outline"
                                className="w-full justify-center gap-2 border-slate-200 dark:border-slate-700"
                            >
                                <Home size={16} /> Voltar para o Início
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
